import type { CarrierProviderAddOnService } from "@/api/carrier";

const DETAIL_LABELS: Record<string, string> = {
  dimFactorCm: "Dim factor (cm)",
  dimFactorInch: "Dim factor (in)",
  coverageMaxCents: "Coverage max",
  currencyCode: "Currency",
  flatFeeCents: "Flat fee",
  percentageRate: "Percentage",
  minimumCents: "Minimum",
  maximumCents: "Maximum",
  dimensionalPricing: "Dimensional pricing",
  notes: "Notes",
};

export interface AddOnDetailRow {
  key: string;
  label: string;
  value: string;
}

export function getAddOnDetail(
  service: CarrierProviderAddOnService
): Record<string, unknown> | null {
  return (
    service.pickupDetails ??
    service.insuranceDetails ??
    service.packagingDetails ??
    service.signatureDetails ??
    service.specialHandlingDetails
  );
}

export function formatCurrencyCents(value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "-";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(value / 100);
}

function formatPercent(value: unknown): string {
  if (typeof value !== "number" || !Number.isFinite(value)) return "-";
  const percent = Math.abs(value) <= 1 ? value * 100 : value;
  return `${Number.parseFloat(percent.toFixed(2))}%`;
}

function humanizeKey(key: string): string {
  return (
    DETAIL_LABELS[key] ??
    key
      .replace(/Cents$/, "")
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase())
  );
}

function formatPrimitiveValue(key: string, value: unknown): string {
  if (key.endsWith("Cents")) return formatCurrencyCents(value);
  if (key === "percentageRate") return formatPercent(value);
  if (typeof value === "number") return String(value);
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "string") return value;
  return String(value);
}

export function formatDetailValue(key: string, value: unknown): string {
  if (value === null || value === undefined) return "-";
  if (Array.isArray(value)) {
    return value.map((item) => formatDetailValue(key, item)).join(", ");
  }
  if (typeof value === "object") {
    const entries = Object.entries(value as Record<string, unknown>)
      .filter(
        ([, nestedValue]) => nestedValue !== null && nestedValue !== undefined
      )
      .map(
        ([nestedKey, nestedValue]) =>
          `${humanizeKey(nestedKey)}: ${formatDetailValue(nestedKey, nestedValue)}`
      );
    return entries.length > 0 ? entries.join("; ") : "-";
  }
  return formatPrimitiveValue(key, value);
}

export function getAddOnDetailRows(
  service: CarrierProviderAddOnService
): AddOnDetailRow[] {
  const detail = getAddOnDetail(service);
  if (!detail) return [];

  return Object.entries(detail)
    .filter(
      ([key, value]) =>
        key !== "carrierAddOnServiceId" && value !== null && value !== undefined
    )
    .map(([key, value]) => ({
      key,
      label: humanizeKey(key),
      value: formatDetailValue(key, value),
    }));
}

export function formatAddOnDetailSummary(
  service: CarrierProviderAddOnService,
  limit = 3
): string {
  const rows = getAddOnDetailRows(service).slice(0, limit);
  if (rows.length === 0) return "-";
  return rows.map((row) => `${row.label}: ${row.value}`).join("; ");
}

export function formatAddOnPricing(
  service: CarrierProviderAddOnService
): string {
  const rows = getAddOnDetailRows(service);
  const byKey = new Map(rows.map((row) => [row.key, row.value]));

  if (byKey.has("flatFeeCents")) return byKey.get("flatFeeCents") as string;
  if (byKey.has("percentageRate")) {
    const parts = [byKey.get("percentageRate") as string];
    if (byKey.has("minimumCents"))
      parts.push(`min ${byKey.get("minimumCents")}`);
    if (byKey.has("maximumCents"))
      parts.push(`max ${byKey.get("maximumCents")}`);
    return parts.join(", ");
  }
  if (byKey.has("dimensionalPricing")) {
    return byKey.get("dimensionalPricing") as string;
  }
  if (byKey.has("dimFactorCm")) return byKey.get("dimFactorCm") as string;
  if (byKey.has("dimFactorInch")) return byKey.get("dimFactorInch") as string;

  return "-";
}
